import React from 'react';
import {
  Box,
  Dialog,
  LoadingIndicator,
  Typography,
} from '@drivekyte/web-components';
import { useMedia } from '@drivekyte/ui';
import styled, { css } from 'styled-components';
import dynamic from 'next/dynamic';

const SearchBar = dynamic(() => import('@/components/search-bar/search-bar'));
const ServiceAreaMap = dynamic(() =>
  import('@/components/service-area-details/components/service-area-map'),
);
const WhereAndWhen = dynamic(() =>
  import('@/components/where-and-when/where-and-when'),
);

import useServiceAreaDetails from '@/hooks/use-service-area-details';
import useTranslation from '@/hooks/use-translation';
import { ServiceArea } from '@/types/service-area';

export type ServiceAreaModalProps = {
  onRequestToClose: () => void;
  serviceArea: ServiceArea;
};

const ServiceAreaModal = ({
  onRequestToClose,
  serviceArea,
}: ServiceAreaModalProps) => {
  const media = useMedia();
  const isMobile = media.xs;
  const { data, isLoading } = useServiceAreaDetails(serviceArea.id, true);
  const { t } = useTranslation('pages.welcome-page.search');

  return (
    <Dialog onRequestToClose={onRequestToClose} visible size="large">
      <Box
        display="flex"
        width="100%"
        flexDirection={isMobile ? 'column' : 'row'}
      >
        <Box
          marginBottom="tiny"
          marginRight={isMobile ? 'none' : 'small'}
          width={isMobile ? '100%' : '60%'}
        >
          {isMobile ? (
            <>
              <Box marginBottom="xSmall">
                <Typography center variant="headline-3" color="primary">
                  {t('modal-title')}
                </Typography>
              </Box>
              <WhereAndWhen />
            </>
          ) : (
            <SearchContainer>
              <Box marginBottom="xSmall">
                <Typography center variant="headline-3" color="primary">
                  {t('modal-title')}
                </Typography>
              </Box>
              <SearchBar buttonPosition="bottom" cta={t('submit')} />
            </SearchContainer>
          )}
        </Box>
        <Box width={isMobile ? '100%' : '40%'} height="100%" minHeight="464px">
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            data && (
              <ServiceAreaMap
                geojson={data.geojson}
                exceptions={[]}
                onExceptionPress={() => {}}
                center={data.center}
              />
            )
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

// TODO: replace with reusable styled search component
const SearchContainer = styled.div`
  ${({ theme }) => css`
    border: ${theme.borderWidths.base}px solid ${theme.borderColors.primary};
    border-radius: ${theme.borderRadius.large}px;
    padding: ${theme.spacings.small}px;
    box-shadow: ${theme.boxShadows.base};
    height: 420px;

    form > div {
      height: auto;
      flex-flow: column wrap;
      border: none;
      box-shadow: none;
    }
    form > div > div {
      box-shadow: ${theme.boxShadows.base};
      border: ${theme.borderWidths.base}px solid ${theme.borderColors.primary};
      &:first-child {
        border-radius: ${theme.borderRadius.base}px;
        margin-bottom: ${theme.spacings.xSmall}px;
      }
      &:nth-child(2) {
        border-radius: ${theme.borderRadius.base}px ${theme.borderRadius.base}px
          0 0;
        border-bottom: none;
        padding: ${theme.spacings.xxSmall};
      }
      &:nth-child(3) {
        display: none;
      }
      &:last-child {
        border-radius: 0 0 ${theme.borderRadius.base}px
          ${theme.borderRadius.base}px;
      }
    }
  `}
`;

export default ServiceAreaModal;
